import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Printer, ArrowLeft, FileText } from "lucide-react";

export default function PrescriptionView() {
  const { petId, prescriptionId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    axios.get(`${API}/pets/${petId}/prescriptions/${prescriptionId}`)
      .then(r => setData(r.data))
      .catch(e => setErr(e?.response?.data?.detail || "Could not load prescription"));
  }, [petId, prescriptionId]);

  if (err) return <div className="text-danger font-bold" data-testid="rx-error">{err}</div>;
  if (!data) return <div className="text-ink-soft">Loading…</div>;

  const { prescription: rx, vet, pet } = data;
  const tpl = vet?.template || {};
  const dateStr = new Date(rx.created_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });

  return (
    <div className="space-y-4" data-testid="rx-view-page">
      <div className="flex items-center justify-between print:hidden">
        <Link to="/app/pets" className="text-sm font-bold text-brand inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4"/> Back</Link>
        <button data-testid="rx-print" onClick={() => window.print()} className="bg-brand text-white rounded-full px-5 py-2.5 font-bold inline-flex items-center gap-2 hover:bg-brand-hover">
          <Printer className="w-4 h-4"/> Print / Save PDF
        </button>
      </div>

      <article id="rx-sheet" className="bg-white text-ink rounded-3xl border border-line shadow-soft max-w-3xl mx-auto p-8 print:shadow-none print:border-0 print:rounded-none print:max-w-none print:p-6" data-testid="rx-sheet">
        {/* Header */}
        <header className="flex items-start justify-between gap-4 border-b border-line pb-4">
          <div className="flex items-center gap-3">
            {tpl.logo_url ? (
              <img src={tpl.logo_url} alt="logo" onError={(e) => { e.currentTarget.style.display = "none"; }} className="h-16 w-16 object-contain bg-surface-muted rounded-2xl p-1"/>
            ) : (
              <div className="h-16 w-16 grid place-items-center bg-brand/10 rounded-2xl text-brand font-heading text-2xl font-extrabold">{(vet?.name || "V")[0]}</div>
            )}
            <div>
              <h1 className="font-heading text-2xl font-extrabold leading-tight">{vet?.name || "Veterinary Clinic"}</h1>
              {tpl.qualifications && <div className="text-xs text-ink-soft font-bold">{tpl.qualifications}</div>}
              {tpl.registration_number && <div className="text-xs text-ink-soft">Reg. No: {tpl.registration_number}</div>}
              {tpl.address && <div className="text-xs text-ink-soft">{tpl.address}</div>}
              {vet?.specialties?.length > 0 && <div className="text-xs text-ink-soft mt-1">{vet.specialties.join(" · ")}</div>}
            </div>
          </div>
          <div className="text-right text-xs text-ink-soft">
            <div className="font-heading text-base font-extrabold text-brand">PRESCRIPTION</div>
            <div>{dateStr}</div>
            <div>Rx #{(rx.prescription_id || "").slice(-6).toUpperCase()}</div>
          </div>
        </header>

        {tpl.header_text && <div className="text-xs italic text-ink-soft mt-3" data-testid="tpl-header-text">{tpl.header_text}</div>}

        {/* Patient block */}
        <section className="mt-5 grid grid-cols-2 gap-4 text-sm">
          <div className="bg-surface-muted rounded-2xl p-3">
            <div className="text-[11px] font-bold uppercase tracking-wider text-ink-soft">Patient</div>
            <div className="font-heading text-lg font-extrabold">{pet?.name}</div>
            <div className="text-ink-soft text-xs">{pet?.species} · {pet?.breed} · {pet?.age_years}y</div>
          </div>
          <div className="bg-surface-muted rounded-2xl p-3">
            <div className="text-[11px] font-bold uppercase tracking-wider text-ink-soft">Diagnosis</div>
            <div className="font-bold">{rx.diagnosis}</div>
            {rx.notes && <div className="text-ink-soft text-xs mt-1">{rx.notes}</div>}
          </div>
        </section>

        {/* Rx items */}
        <section className="mt-5">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-3xl font-heading font-extrabold text-brand leading-none">℞</span>
            <span className="font-heading text-lg font-bold">Prescription</span>
          </div>
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-surface-muted text-left">
                <th className="px-3 py-2 border-b border-line">#</th>
                <th className="px-3 py-2 border-b border-line">Medicine</th>
                <th className="px-3 py-2 border-b border-line">Dose</th>
                <th className="px-3 py-2 border-b border-line">Frequency</th>
                <th className="px-3 py-2 border-b border-line">Duration</th>
              </tr>
            </thead>
            <tbody>
              {(rx.items || []).map((it, i) => (
                <tr key={i} className="border-b border-line align-top" data-testid={`rx-row-${i}`}>
                  <td className="px-3 py-2 font-bold">{i + 1}</td>
                  <td className="px-3 py-2">
                    <div className="font-bold">{it.medicine_name} {it.strength && <span className="text-ink-soft font-normal">{it.strength}</span>}</div>
                    {it.notes && <div className="text-xs text-ink-soft">{it.notes}</div>}
                  </td>
                  <td className="px-3 py-2">{it.dose || "—"}</td>
                  <td className="px-3 py-2">{it.frequency || "—"}</td>
                  <td className="px-3 py-2">{it.duration || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {/* Footer / signature */}
        <footer className="mt-8 flex items-end justify-between">
          <div className="text-[11px] text-ink-soft italic max-w-md">
            {tpl.footer_text || "Please follow the medication schedule as advised. Contact your veterinarian for any side effects."}
          </div>
          <div className="text-center">
            {tpl.signature_url ? (
              <img src={tpl.signature_url} alt="signature" onError={(e) => { e.currentTarget.style.display = "none"; }} className="h-14 object-contain mx-auto"/>
            ) : (
              <div className="h-14 w-40 border-b-2 border-dashed border-line"/>
            )}
            <div className="text-xs font-bold mt-1">{vet?.name}</div>
            <div className="text-[11px] text-ink-soft">Signature</div>
          </div>
        </footer>
      </article>

      <style>{`
        @media print {
          body { background: #fff !important; }
          .print\\:hidden { display: none !important; }
          #rx-sheet { box-shadow: none !important; border: 0 !important; }
        }
      `}</style>
    </div>
  );
}
